import { Container, Flex, Image, Toaster } from '@grupoboticario/flora-react';
import { MagazineIcon } from '@grupoboticario/flora-react-icons';
import { useNavigate, useParams } from 'react-router-dom';
import slugify from 'slugify';

import { Button, EmptyState, PageInfo, Skeleton, ResponsiveFloraTypography } from 'components';
import { isMobile as isMobileHelper } from 'helpers';
import { useStarlinkContent, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';

import { InfoOnboarding, ModalShare } from './components';
import Styles from './ImagesToShare.styles';

import type { ImagesToShareProps, ImagesToShareStarlinkType } from 'domains/images-to-share';
import type { StarlinkResponse } from 'domains/starlink';

export const FullImagesToShare = () => {
  const isMobile = isMobileHelper();
  const { content: paramContent = '' } = useParams();
  const content = decodeURIComponent(paramContent);
  const {
    data: starlinkData,
    isLoading: isLoadingStarlinkContent,
    isSuccess: isSuccessStarlinkContent,
    isError: isErrorStarlinkContent,
  } = useStarlinkContent<ImagesToShareStarlinkType>({
    document: 'content_to_share',
    page_size: 50,
  });
  const starlinkContent = starlinkData?.items as unknown as Array<
    StarlinkResponse<unknown, ImagesToShareProps>
  >;
  const pageContent =
    starlinkContent &&
    (starlinkContent.find((item) => item.data.title === content)?.data as ImagesToShareProps);
  const navigate = useNavigate();
  const { sendDataToGTM } = useTracking();

  if (!pageContent && !isLoadingStarlinkContent && isErrorStarlinkContent) {
    newRelicMonitoringService.addPageAction('pageview_imagens_divulgacao', {
      cd_detail: `erro:nao-foi-possivel-encontrar-conteudo`,
    });

    navigate('/imagens-de-divulgacao');

    Toaster.notify({
      kind: 'error',
      description: `Não foi possível encontrar o conteúdo ${content}.`,
      origin: 'right-top',
    });

    return null;
  }

  const handleDownloadAll = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_img_divulgacao',
        cd_interaction_detail: 'click:baixar-todas-as-imagens',
        cd_quantity: pageContent.listToShare?.length.toString(),
        cd_section: slugify(pageContent.title || '', { lower: true }),
      },
    });
  };

  return (
    <>
      <Container>
        <PageInfo
          breadcrumbs={[
            {
              text: 'Imagens de divulgação',
              url: '/imagens-de-divulgacao',
            },
            {
              text: content || 'categoria',
              url: '',
            },
          ]}
        >
          {isLoadingStarlinkContent ? (
            <>
              <Skeleton height='$12' width='25%' />
              <Skeleton height='$7' width='50%' />
            </>
          ) : (
            <>
              <ResponsiveFloraTypography as='h1' fontSize='title' fontWeight='bold'>
                {pageContent.title}
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography>{pageContent.description}</ResponsiveFloraTypography>
            </>
          )}
        </PageInfo>
      </Container>
      <Styles.Wrapper>
        <Container size='large'>
          <Flex direction='column' gap='$4'>
            <Flex justify='flex-start'>
              {isLoadingStarlinkContent ? (
                <Skeleton height='$7' width='$23' />
              ) : (
                <Button
                  variation='DownloadAll'
                  variantProps={{
                    items: pageContent.listToShare?.map((item) => item.image) || [],
                    onClick: handleDownloadAll,
                  }}
                />
              )}
            </Flex>
            {isSuccessStarlinkContent && <InfoOnboarding />}

            <Styles.ListImagesWrapper>
              {isLoadingStarlinkContent &&
                [...Array(isMobile ? 1 : 4)].map((_, index) => (
                  <Styles.Card key={`image-${index}`} padded={false}>
                    <Styles.SkeletonImage key={index} data-testid='skeleton-image' />
                  </Styles.Card>
                ))}

              {isSuccessStarlinkContent &&
                pageContent.listToShare?.length &&
                pageContent.listToShare?.map((item, index) => (
                  <Styles.Card key={`image-${pageContent.title}-${index}`} padded={false}>
                    <ModalShare data={pageContent} indice={index + 1} imageItem={item}>
                      <Image loading='lazy' src={item.image.url || ''} alt={item.image.alt || ''} />
                    </ModalShare>
                  </Styles.Card>
                ))}

              {isSuccessStarlinkContent && !pageContent.listToShare?.length && (
                <Flex gap='$6' justify='center'>
                  <EmptyState
                    title='Materiais em preparação.'
                    subtitle='Em breve, as imagens de divulgação estarão disponíveis aqui.'
                  >
                    <MagazineIcon color='$backgroundAltPrimary' size={80} />
                  </EmptyState>
                </Flex>
              )}
            </Styles.ListImagesWrapper>
          </Flex>
        </Container>
      </Styles.Wrapper>
    </>
  );
};
